// eslint-disable no-unused-vars
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { testTemplateAPI } from '../../api/testTemplateApi';
import { testDataAPI } from '../../api/testDataApi';
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ScatterChart as RechartsScatterChart,
  Scatter,
  ZAxis
} from 'recharts';
import SearchableSelect from '../../components/Portal/SearchableSelect';
import * as S from './MockTestTransformation.styles';

const MockTestTransformation = ({ backPath }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const dashboardPath = backPath || '/dashboard';

  // Data states
  const [testTemplates, setTestTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransforming, setIsTransforming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Transformation data
  const [originalScores, setOriginalScores] = useState(null);
  const [transformedData, setTransformedData] = useState(null);
  const [transformationParams, setTransformationParams] = useState({
    QR: { min: 0, max: 100, mean: 50, std_dev: 15 },
    AR: { min: 0, max: 100, mean: 50, std_dev: 15 },
    Reading: { min: 0, max: 100, mean: 50, std_dev: 15 },
    Writing: { min: 0, max: 100, mean: 50, std_dev: 15 },
    total: { min: 0, max: 400, mean: 200, std_dev: 50 }
  });
  
  // Student-specific offsets
  const [studentOffsets, setStudentOffsets] = useState({});
  
  // Local offset state for immediate UI updates
  const [localOffsets, setLocalOffsets] = useState({});
  
  // UI states
  const [activeTab, setActiveTab] = useState('overview');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [histogramBins, setHistogramBins] = useState(12);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Permission check
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const isAdmin = userGroups.includes('site_admin');
  const hasPortalPermission = userGroups.includes('portal_developer') || userGroups.includes('portal_system_admin') || userGroups.includes('portal_director') || userGroups.includes('portal_manager');
  const hasPermission = isAdmin || hasPortalPermission;

  // Helper functions to calculate total scores from sections
  const calculateTotalScoreFromSections = (sections) => {
    const sectionNames = ['QR', 'AR', 'Reading', 'Writing'];
    return sectionNames.reduce((total, sectionName) => {
      const sectionScore = sections?.[sectionName]?.score || 0;
      return total + sectionScore;
    }, 0);
  };

  const calculateOriginalTotalScore = (originalScores) => {
    const sectionNames = ['QR', 'AR', 'Reading', 'Writing'];
    return sectionNames.reduce((total, sectionName) => {
      const sectionScore = originalScores?.sections?.[sectionName]?.score || 0;
      return total + sectionScore;
    }, 0);
  };

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      if (!auth.isAuthenticated || !hasPermission) return;
      
      setIsLoading(true);
      try {
        const templates = await testTemplateAPI.getAllTemplates(auth.user?.id_token);
        const mockTestTemplates = templates.filter(template => {
          const templateType = template.templateData?.templateType || 'mockTest';
          return templateType === 'mockTest';
        });
        setTestTemplates(mockTestTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [auth.isAuthenticated, auth.user?.id_token, hasPermission]);

  // Load scores when template is selected
  useEffect(() => {
    const loadScores = async () => {
      if (!selectedTemplateId || !auth.user?.id_token) {
        setOriginalScores(null);
        setTransformedData(null);
        setStudentOffsets({});
        setLocalOffsets({});
        return;
      }
      
      setIsTransforming(true);
      try {
        const template = testTemplates.find(t => t.id === selectedTemplateId);
        setSelectedTemplate(template);
        
        // Load saved parameters if they exist
        const params = template?.mockParameters || transformationParams;
        if (template?.mockParameters) {
          const { studentOffsets, ...transformParams } = template.mockParameters;
          
          setTransformationParams(prev => ({
            ...prev,
            ...transformParams
          }));
          
          // Load student offsets if they exist, otherwise clear them
          if (studentOffsets) {
            setStudentOffsets(studentOffsets);
            setLocalOffsets(studentOffsets);
          } else {
            setStudentOffsets({});
            setLocalOffsets({});
          }
        } else {
          // No saved parameters, clear offsets
          setStudentOffsets({});
          setLocalOffsets({});
        }
        
        // Load both original and transformed scores in one call
        const result = await testDataAPI.calculateTransformedMockTestScores(
          selectedTemplateId,
          params,
          auth.user.id_token
        );
        
        // Extract original scores from the result
        const originalScoresData = {
          studentScores: result.studentScores.map(s => ({
            studentId: s.studentId,
            studentName: s.studentName,
            group: s.group,
            totalScore: s.originalScores.totalScore,
            totalMaxScore: s.originalScores.totalMaxScore,
            totalPercentage: s.originalScores.totalPercentage,
            sections: s.originalScores.sections
          })),
          summary: {
            averageScore: result.statistics?.originalStats?.mean || 0
          }
        };
        
        setOriginalScores(originalScoresData);
        setTransformedData(result);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error loading scores:', error);
        setOriginalScores(null);
        setTransformedData(null);
      } finally {
        setIsTransforming(false);
      }
    };

    loadScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, auth.user?.id_token, testTemplates]);

  // Transform scores with current parameters
  const transformScores = useCallback(async () => {
    if (!selectedTemplateId || !auth.user?.id_token || !originalScores) return;
    
    setIsTransforming(true);
    try {
      const parametersWithOffsets = {
        ...transformationParams,
        studentOffsets: studentOffsets
      };
      
      const result = await testDataAPI.calculateTransformedMockTestScores(
        selectedTemplateId,
        parametersWithOffsets,
        auth.user.id_token
      );
      
      setTransformedData(result);
      
      // Update original scores if needed (in case there are new students)
      if (result.studentScores.length !== originalScores.studentScores.length) {
        const originalScoresData = {
          studentScores: result.studentScores.map(s => ({
            studentId: s.studentId,
            studentName: s.studentName,
            group: s.group,
            totalScore: s.originalScores.totalScore,
            totalMaxScore: s.originalScores.totalMaxScore,
            totalPercentage: s.originalScores.totalPercentage,
            sections: s.originalScores.sections
          })),
          summary: {
            averageScore: result.statistics?.originalStats?.mean || 0
          }
        };
        setOriginalScores(originalScoresData);
      }
    } catch (error) {
      console.error('Error transforming scores:', error);
    } finally {
      setIsTransforming(false);
    }
  }, [selectedTemplateId, transformationParams, studentOffsets, auth.user?.id_token, originalScores]);

  // Transform scores when parameters change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedTemplateId && transformationParams && originalScores) {
        transformScores();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [transformationParams, studentOffsets, transformScores, selectedTemplateId, originalScores]);

  // Save parameters to template
  const saveParameters = async () => {
    if (!selectedTemplateId || !auth.user?.id_token || !hasUnsavedChanges) return false;
    
    setIsSaving(true);
    try {
      const parametersToSave = {
        ...transformationParams,
        studentOffsets: studentOffsets
      };
      
      await testTemplateAPI.updateMockParameters(
        selectedTemplateId,
        parametersToSave,
        auth.user.id_token
      );
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      console.log('Parameters auto-saved successfully');
      return true;
    } catch (error) {
      console.error('Error saving parameters:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save parameters every 10 seconds if changed
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      if (hasUnsavedChanges && !isSaving) {
        console.log('Auto-saving parameters...');
        await saveParameters();
      }
    }, 10000); // 10 seconds

    return () => clearInterval(autoSaveInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, isSaving]);

  // Handle parameter changes with validation
  const handleParameterChange = (section, param, value) => {
    const numValue = parseFloat(value);
    
    setTransformationParams(prev => {
      const currentParams = prev[section];
      let newParams = { ...currentParams };
      
      // Update the changed parameter
      newParams[param] = numValue;
      
      // Enforce constraints: min < mean < max
      if (param === 'min') {
        // If min is changed, ensure mean and max are greater than min
        if (newParams.mean <= numValue) {
          newParams.mean = numValue + 1;
        }
        if (newParams.max <= newParams.mean) {
          newParams.max = newParams.mean + 1;
        }
      } else if (param === 'max') {
        // If max is changed, ensure mean and min are less than max
        if (newParams.mean >= numValue) {
          newParams.mean = numValue - 1;
        }
        if (newParams.min >= newParams.mean) {
          newParams.min = newParams.mean - 1;
        }
      } else if (param === 'mean') {
        // If mean is changed, ensure it's between min and max
        if (numValue <= newParams.min) {
          newParams.min = numValue - 1;
        }
        if (numValue >= newParams.max) {
          newParams.max = numValue + 1;
        }
      }
      
      return {
        ...prev,
        [section]: newParams
      };
    });
    
    setHasUnsavedChanges(true);
  };

  // Calculate top 30% students for highlighting
  const getTop30PercentStudents = useCallback((section) => {
    if (!transformedData?.studentScores || transformedData.studentScores.length === 0) {
      return { topStudents: new Set(), percentileInfo: null };
    }
    
    const isTotal = section === 'total';
    const studentsWithScores = transformedData.studentScores
      .filter(student => student?.transformedScores) // Ensure transformedScores exists
      .map((student, index) => ({
        studentId: student.studentId,
        studentName: student.studentName,
        score: isTotal 
          ? calculateTotalScoreFromSections(student.transformedScores.sections)
          : (student.transformedScores.sections?.[section]?.score || 0),
        index
      }))
      .filter(student => student.score !== undefined && student.score !== null); // Filter out invalid scores
    
    if (studentsWithScores.length === 0) {
      return { topStudents: new Set(), percentileInfo: null };
    }
    
    // Sort by score descending
    studentsWithScores.sort((a, b) => b.score - a.score);
    
    // Calculate top 30% (round up)
    const totalStudents = studentsWithScores.length;
    const top30PercentCount = Math.ceil(totalStudents * 0.3);
    const topStudents = new Set(studentsWithScores.slice(0, top30PercentCount).map(s => s.studentId));
    
    // Get 30th percentile student info (approximately)
    const percentileIndex = Math.min(top30PercentCount - 1, totalStudents - 1);
    const percentileStudent = studentsWithScores[percentileIndex];
    const percentileInfo = percentileStudent ? {
      name: percentileStudent.studentName,
      rank: percentileIndex + 1,
      total: totalStudents,
      score: percentileStudent.score
    } : null;
    
    return { topStudents, percentileInfo };
  }, [transformedData]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort students based on current sort configuration
  const getSortedStudents = (students, section) => {
    if (!sortConfig.key) return students;
    
    const isTotal = section === 'total';
    
    return [...students].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'name':
          aValue = a.studentName;
          bValue = b.studentName;
          break;
        case 'original':
          aValue = isTotal ? calculateOriginalTotalScore(a.originalScores) : a.originalScores.sections[section]?.score || 0;
          bValue = isTotal ? calculateOriginalTotalScore(b.originalScores) : b.originalScores.sections[section]?.score || 0;
          break;
        case 'transformed':
          aValue = isTotal ? calculateTotalScoreFromSections(a.transformedScores.sections) : a.transformedScores.sections[section]?.score || 0;
          bValue = isTotal ? calculateTotalScoreFromSections(b.transformedScores.sections) : b.transformedScores.sections[section]?.score || 0;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle offset changes for individual students
  const handleStudentOffsetChange = (studentId, offsetType, section, value) => {
    const numValue = parseFloat(value) || 0;
    
    // Update local offset state immediately for UI responsiveness
    setLocalOffsets(prev => {
      const studentData = prev[studentId] || { sections: {}, total: 0 };
      
      if (offsetType === 'sections') {
        return {
          ...prev,
          [studentId]: {
            ...studentData,
            sections: {
              ...studentData.sections,
              [section]: numValue
            }
          }
        };
      } else {
        return {
          ...prev,
          [studentId]: {
            ...studentData,
            total: numValue
          }
        };
      }
    });
    
    // Update actual offset state for API calls
    setStudentOffsets(prev => {
      const studentData = prev[studentId] || { sections: {}, total: 0 };
      
      if (offsetType === 'sections') {
        return {
          ...prev,
          [studentId]: {
            ...studentData,
            sections: {
              ...studentData.sections,
              [section]: numValue
            }
          }
        };
      } else {
        return {
          ...prev,
          [studentId]: {
            ...studentData,
            total: numValue
          }
        };
      }
    });
    
    setHasUnsavedChanges(true);
  };


  // Render parameter controls
  const renderParameterControls = (section, title) => {
    const params = transformationParams[section];
    const isTotal = section === 'total';
    
    // Hide parameter controls for total score - it's calculated from sections
    if (isTotal) {
      return (
        <div style={{ 
          marginBottom: '30px',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          position: 'sticky',
          top: '80px',
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h4 style={{ 
            textAlign: 'center', 
            color: '#495057',
            fontSize: '18px',
            margin: 0
          }}>
            {title} - Calculated from 4 Sections
          </h4>
          <p style={{ 
            textAlign: 'center', 
            color: '#6c757d',
            fontSize: '14px',
            margin: '10px 0 0 0'
          }}>
            Total score is automatically calculated as the sum of QR + AR + Reading + Writing sections
          </p>
        </div>
      );
    }
    
    // Define tab order for navigation
    const tabOrder = ['qr', 'ar', 'reading', 'writing', 'total'];
    const currentIndex = tabOrder.indexOf(activeTab);
    const prevTab = currentIndex > 0 ? tabOrder[currentIndex - 1] : null;
    const nextTab = currentIndex < tabOrder.length - 1 ? tabOrder[currentIndex + 1] : null;
    
    const getTabLabel = (tab) => {
      switch(tab) {
        case 'qr': return 'QR';
        case 'ar': return 'AR';
        case 'reading': return 'Reading';
        case 'writing': return 'Writing';
        case 'total': return 'Total';
        default: return tab;
      }
    };
    
    return (
      <div style={{ 
        marginBottom: '30px',
        padding: '20px',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        position: 'sticky',
        top: '80px',
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {prevTab && (
            <button
              onClick={() => setActiveTab(prevTab)}
              style={{
                position: 'absolute',
                left: 0,
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              ← {getTabLabel(prevTab)}
            </button>
          )}
          
          <h4 style={{ 
            textAlign: 'center', 
            color: '#495057',
            fontSize: '18px',
            margin: 0
          }}>
            {title} Parameters
          </h4>
          
          {nextTab && (
            <button
              onClick={() => setActiveTab(nextTab)}
              style={{
                position: 'absolute',
                right: 0,
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              {getTabLabel(nextTab)} →
            </button>
          )}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div>
            <label style={{ fontSize: '14px', color: '#6c757d', display: 'block' }}>
              Min: {params.min}
            </label>
            <input
              type="range"
              min={0}
              max={Math.min(params.mean - 1, isTotal ? 399 : 99)}
              value={params.min}
              onChange={(e) => handleParameterChange(section, 'min', e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '14px', color: '#6c757d', display: 'block' }}>
              Max: {params.max}
            </label>
            <input
              type="range"
              min={Math.max(params.mean + 1, 1)}
              max={isTotal ? 400 : 100}
              value={params.max}
              onChange={(e) => handleParameterChange(section, 'max', e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '14px', color: '#6c757d', display: 'block' }}>
              Mean: {params.mean}
            </label>
            <input
              type="range"
              min={params.min + 1}
              max={params.max - 1}
              value={params.mean}
              onChange={(e) => handleParameterChange(section, 'mean', e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '14px', color: '#6c757d', display: 'block' }}>
              Std Dev: {params.std_dev}
            </label>
            <input
              type="range"
              min={1}
              max={isTotal ? 100 : 50}
              value={params.std_dev}
              onChange={(e) => handleParameterChange(section, 'std_dev', e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Render student offset table
  const renderStudentOffsetTable = (section) => {
    if (!transformedData || !originalScores) return null;

    const isTotal = section === 'total';
    const { topStudents, percentileInfo } = getTop30PercentStudents(section);
    const sortedStudents = getSortedStudents(transformedData.studentScores, section);

    // Calculate rank map based on final score (only for total section)
    const rankMap = {};
    if (isTotal) {
      const studentsWithFinalScore = transformedData.studentScores.map(student => ({
        studentId: student.studentId,
        finalScore: calculateTotalScoreFromSections(student.transformedScores.sections)
      }));
      // Sort by final score descending
      studentsWithFinalScore.sort((a, b) => b.finalScore - a.finalScore);
      // Assign ranks
      studentsWithFinalScore.forEach((student, index) => {
        rankMap[student.studentId] = index + 1;
      });
    }
    
    const getSortIcon = (key) => {
      if (sortConfig.key !== key) return ' ↕️';
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };
    
    return (
      <div style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h4 style={{ margin: 0, color: '#495057' }}>Student Score Adjustments</h4>
          {percentileInfo && (
            <div style={{
              background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
              color: 'white',
              padding: '8px 15px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              30th Percentile: {percentileInfo.name} (Rank {percentileInfo.rank}/{percentileInfo.total}, Score: {percentileInfo.score.toFixed(1)})
            </div>
          )}
        </div>
        
        <div style={{ 
          overflowX: 'auto',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#e9ecef' }}>
                {isTotal && (
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '60px' }}>Rank</th>
                )}
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('name')}
                >
                  Student Name{getSortIcon('name')}
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Student ID</th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('original')}
                >
                  Original Score{getSortIcon('original')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Offset</th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('transformed')}
                >
                  Transformed Score{getSortIcon('transformed')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.map((student, index) => {
                const originalScore = isTotal 
                  ? calculateOriginalTotalScore(student.originalScores)
                  : student.originalScores.sections[section]?.score || 0;
                  
                // The backend returns the final transformed score (with offsets already applied)
                const transformedScore = isTotal 
                  ? calculateTotalScoreFromSections(student.transformedScores.sections)
                  : student.transformedScores.sections[section]?.score || 0;
                  
                // Get the current offset value from backend data if it exists
                const backendOffset = isTotal 
                  ? student.transformedScores.totalOffset || 0
                  : student.transformedScores.sections[section]?.offset || 0;
                  
                // Get local offset value for immediate UI updates
                const localOffset = isTotal 
                  ? localOffsets[student.studentId]?.total || 0
                  : localOffsets[student.studentId]?.sections?.[section] || 0;
                  
                // Use local offset if it exists, otherwise use backend offset
                const displayOffsetValue = localOffset !== 0 ? localOffset : backendOffset;
                const hasOffset = displayOffsetValue !== 0;
                const isTopStudent = topStudents.has(student.studentId);
                
                let backgroundColor = 'white';
                if (isTopStudent) {
                  backgroundColor = '#ffebee'; // Light red for top 30%
                } else if (hasOffset) {
                  backgroundColor = '#fff3cd'; // Yellow for offset
                }
                
                return (
                  <tr key={student.studentId} style={{
                    backgroundColor,
                    borderBottom: '1px solid #dee2e6',
                    borderLeft: isTopStudent ? '4px solid #f44336' : 'none'
                  }}>
                    {isTotal && (
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: '#495057'
                      }}>
                        {rankMap[student.studentId]}
                      </td>
                    )}
                    <td style={{ padding: '12px', fontWeight: '500' }}>{student.studentName}</td>
                    <td style={{ padding: '12px', color: '#6c757d', fontSize: '14px' }}>{student.studentId}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{originalScore.toFixed(1)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleStudentOffsetChange(
                            student.studentId,
                            isTotal ? 'total' : 'sections',
                            isTotal ? null : section,
                            displayOffsetValue - 0.5
                          )}
                          style={{
                            padding: '2px 6px',
                            border: '1px solid #ced4da',
                            borderRadius: '3px',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↓
                        </button>
                        <input
                          type="number"
                          value={displayOffsetValue}
                          onChange={(e) => handleStudentOffsetChange(
                            student.studentId,
                            isTotal ? 'total' : 'sections',
                            isTotal ? null : section,
                            e.target.value
                          )}
                          style={{ 
                            width: '60px',
                            padding: '6px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            textAlign: 'center'
                          }}
                          step="0.5"
                          min="-50"
                          max="50"
                        />
                        <button
                          onClick={() => handleStudentOffsetChange(
                            student.studentId,
                            isTotal ? 'total' : 'sections',
                            isTotal ? null : section,
                            displayOffsetValue + 0.5
                          )}
                          style={{
                            padding: '2px 6px',
                            border: '1px solid #ced4da',
                            borderRadius: '3px',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↑
                        </button>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '12px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: hasOffset ? '#dc3545' : '#28a745'
                    }}>
                      {transformedScore.toFixed(1)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {hasOffset && (
                        <button
                          onClick={() => handleStudentOffsetChange(
                            student.studentId,
                            isTotal ? 'total' : 'sections',
                            isTotal ? null : section,
                            0
                          )}
                          style={{ 
                            cursor: 'pointer',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Summary Statistics */}
        <div style={{ 
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#e9ecef',
          borderRadius: '6px',
          fontSize: '14px',
          color: '#495057'
        }}>
          <strong>Adjustment Summary:</strong> {
            transformedData.studentScores.reduce((count, student) => {
              const hasAnyOffset = isTotal 
                ? (student.transformedScores.totalOffset || 0) !== 0
                : (student.transformedScores.sections[section]?.offset || 0) !== 0;
              return count + (hasAnyOffset ? 1 : 0);
            }, 0)
          } students with adjustments
        </div>
      </div>
    );
  };

  // Render comparison chart
  const renderComparisonChart = (section, title) => {
    if (!transformedData || !originalScores) return null;
    
    const originalData = originalScores.studentScores?.map(s => 
      section === 'total' ? calculateOriginalTotalScore(s) : s.sections?.[section]?.score || 0
    ) || [];
    
    const transformedScores = transformedData.studentScores?.map(s => 
      section === 'total' ? calculateTotalScoreFromSections(s.transformedScores.sections) : s.transformedScores.sections?.[section]?.score || 0
    ) || [];
    
    // Create scatter data with student info (transformed score as x-axis)
    const scatterData = originalData.map((orig, i) => {
      const studentData = transformedData.studentScores[i];
      return {
        x: transformedScores[i],
        y: orig,
        id: i,
        studentName: studentData?.studentName || `Student ${i + 1}`
      };
    });
    
    // Create histogram data with actual score ranges
    const createHistogram = (data, label, color, globalMin, globalMax) => {
      const bins = histogramBins; // Use dynamic bin count
      const binSize = (globalMax - globalMin) / bins;
      
      const histogram = Array(bins).fill(0);
      const binLabels = [];
      
      data.forEach(value => {
        const binIndex = Math.min(Math.floor((value - globalMin) / binSize), bins - 1);
        histogram[binIndex]++;
      });
      
      // Create labels with actual score ranges
      for (let i = 0; i < bins; i++) {
        const rangeStart = globalMin + i * binSize;
        const rangeEnd = globalMin + (i + 1) * binSize;
        binLabels.push(`${rangeStart.toFixed(1)}-${rangeEnd.toFixed(1)}`);
      }
      
      return {
        data: histogram,
        label,
        color,
        binLabels
      };
    };
    
    // Calculate global min/max for consistent binning
    const allData = [...originalData, ...transformedScores];
    const globalMin = Math.min(...allData);
    const globalMax = Math.max(...allData);
    
    const originalHist = createHistogram(originalData, 'Original', '#3498db', globalMin, globalMax);
    const transformedHist = createHistogram(transformedScores, 'Transformed', '#e74c3c', globalMin, globalMax);
    
    return (
      <div style={{ marginBottom: '40px' }}>
        <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>{title}</h3>
        
        {/* Score Correlation with Recharts - Full width (moved to top) */}
        <div style={{ marginBottom: '30px' }}>
          <h4 style={{ textAlign: 'center', marginBottom: '10px' }}>Score Correlation</h4>
          <div style={{ width: '100%', height: '500px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsScatterChart
                margin={{ top: 20, right: 50, left: 70, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name="Transformed Score"
                  label={{ value: 'Transformed Score', position: 'insideBottom', offset: -10, style: { fill: '#000' } }}
                  tick={{ fontSize: 12, fill: '#000' }}
                  stroke="#000"
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name="Original Score"
                  label={{ value: 'Original Score', angle: -90, position: 'insideLeft', style: { fill: '#000' } }}
                  tick={{ fontSize: 12, fill: '#000' }}
                  stroke="#000"
                />
                <ZAxis range={[60, 60]} />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{data.studentName}</p>
                          <p style={{ margin: '2px 0', color: '#9b59b6' }}>
                            Transformed: {data.x.toFixed(1)}
                          </p>
                          <p style={{ margin: '2px 0', color: '#9b59b6' }}>
                            Original: {data.y.toFixed(1)}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter 
                  name="Score Correlation" 
                  data={scatterData} 
                  fill="#9b59b6"
                  fillOpacity={0.6}
                />
              </RechartsScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Distribution Comparison with Recharts - Full width */}
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '15px' 
          }}>
            <h4 style={{ textAlign: 'center', margin: 0 }}>Distribution Comparison</h4>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              background: '#f8f9fa',
              padding: '8px 15px',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <label style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                Bins: {histogramBins}
              </label>
              <input
                type="range"
                min={6}
                max={50}
                value={histogramBins}
                onChange={(e) => setHistogramBins(parseInt(e.target.value))}
                style={{ width: '150px' }}
              />
            </div>
          </div>
          <div style={{ width: '100%', height: '750px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={originalHist.binLabels.map((label, index) => ({
                  name: label,
                  original: originalHist.data[index],
                  transformed: transformedHist.data[index],
                  originalTrend: originalHist.data[index],
                  transformedTrend: transformedHist.data[index]
                }))}
                margin={{ top: 20, right: 50, left: 60, bottom: 120 }}
              >
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  label={{ value: 'Score Range', position: 'insideBottom', offset: -110, style: { fill: '#000' } }}
                  tick={{ fill: '#000' }}
                  stroke="#000"
                />
                <YAxis 
                  label={{ value: 'Number of Students', angle: -90, position: 'insideLeft', style: { fill: '#000' } }}
                  tick={{ fill: '#000' }}
                  stroke="#000"
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
                          {payload
                            .filter(entry => entry.dataKey === 'original' || entry.dataKey === 'transformed')
                            .map((entry, index) => (
                              <p key={index} style={{ margin: '2px 0', color: entry.color }}>
                                {entry.name}: {entry.value}
                              </p>
                            ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  content={({ payload }) => {
                    const filteredPayload = payload.filter(
                      entry => entry.dataKey === 'original' || entry.dataKey === 'transformed'
                    );
                    return (
                      <ul style={{ 
                        listStyle: 'none', 
                        padding: 0, 
                        margin: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '20px'
                      }}>
                        {filteredPayload.map((entry, index) => (
                          <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{
                              backgroundColor: entry.color,
                              width: '18px',
                              height: '18px',
                              display: 'inline-block',
                              marginRight: '8px'
                            }} />
                            <span>{entry.value}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }}
                />
                
                {/* Bar charts */}
                <Bar dataKey="original" fill="#3498db" name="Original" />
                <Bar dataKey="transformed" fill="#e74c3c" name="Transformed" />
                
                {/* Smooth trend lines connecting apex points - no name to exclude from legend */}
                <Line 
                  type="monotone" 
                  dataKey="originalTrend" 
                  stroke="#2563eb" 
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="transformedTrend" 
                  stroke="#dc2626" 
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Render statistics
  const renderStatistics = () => {
    if (!transformedData || !originalScores) return null;
    
    const { originalStats, transformedStats } = transformedData.statistics || {};
    const { rankPreservationMetrics } = transformedData;
    
    return (
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px',
        marginBottom: '30px'
      }}>
        <S.StatCard>
          <S.StatTitle>Original Mean</S.StatTitle>
          <S.StatValue>{originalStats?.mean?.toFixed(1) || 0}</S.StatValue>
        </S.StatCard>
        <S.StatCard>
          <S.StatTitle>Transformed Mean</S.StatTitle>
          <S.StatValue>{transformedStats?.mean?.toFixed(1) || 0}</S.StatValue>
        </S.StatCard>
        <S.StatCard>
          <S.StatTitle>Rank Correlation</S.StatTitle>
          <S.StatValue>{rankPreservationMetrics?.spearman_correlation?.toFixed(4) || 0}</S.StatValue>
        </S.StatCard>
        <S.StatCard>
          <S.StatTitle>Rank Preservation</S.StatTitle>
          <S.StatValue>{(rankPreservationMetrics?.rank_preservation_rate * 100)?.toFixed(1) || 0}%</S.StatValue>
        </S.StatCard>
      </div>
    );
  };

  // Permission check
  if (!auth.isAuthenticated) {
    navigate(backPath ? '/portal/login' : '/login');
    return null;
  }

  if (!hasPermission) {
    return (
      <S.Container>
        <S.Header>
          <S.Title>Access Denied</S.Title>
        </S.Header>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h2>You don't have permission to access this page</h2>
          <p>Only administrators can access mock test transformation.</p>
          <S.Button onClick={() => navigate(dashboardPath)}>Back to Dashboard</S.Button>
        </div>
      </S.Container>
    );
  }

  if (isLoading) {
    return (
      <S.Container>
        <S.Header>
          <S.Title>Mock Test Score Transformation</S.Title>
          <S.Button onClick={() => navigate(dashboardPath)}>Back to Dashboard</S.Button>
        </S.Header>
        <S.EmptyState>
          <h3>Loading...</h3>
          <p>Loading mock test templates...</p>
        </S.EmptyState>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <S.Title>Mock Test Score Transformation</S.Title>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {hasUnsavedChanges ? (
            <S.Button 
              onClick={saveParameters}
              disabled={isSaving}
              style={{ backgroundColor: '#28a745' }}
            >
              {isSaving ? 'Saving...' : 'Save Now'}
            </S.Button>
          ) : lastSaved && (
            <span style={{ 
              color: '#28a745', 
              fontSize: '14px',
              fontStyle: 'italic'
            }}>
              Auto-saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <S.Button onClick={() => navigate(dashboardPath)}>Back to Dashboard</S.Button>
        </div>
      </S.Header>

      {/* Template Selection */}
      <S.FilterSection>
        <S.FilterGroup>
          <S.Label htmlFor="mock-transformation-template">Select Mock Test Template:</S.Label>
          <SearchableSelect
            id="mock-transformation-template"
            value={selectedTemplateId}
            onChange={(templateId) => setSelectedTemplateId(templateId || '')}
            placeholder="Search or select a template..."
            noOptionsText="No template matches"
            options={testTemplates
              .map(template => ({
                value: template.id,
                label: template.templateData?.examName || template.templateName || 'Unnamed Template',
              }))
              .sort((a, b) => a.label.localeCompare(b.label))}
          />
        </S.FilterGroup>
        
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          {isTransforming && (
            <div style={{ color: '#6c757d', fontSize: '14px' }}>
              Transforming scores...
            </div>
          )}
          {hasUnsavedChanges && !isSaving && (
            <div style={{ color: '#ffc107', fontSize: '14px', fontStyle: 'italic' }}>
              Changes will auto-save in 10 seconds
            </div>
          )}
          {isSaving && (
            <div style={{ color: '#17a2b8', fontSize: '14px' }}>
              Auto-saving...
            </div>
          )}
        </div>
      </S.FilterSection>

      {/* Navigation Tabs */}
      {selectedTemplateId && (
        <S.TabContainer>
          <S.Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            Overview
          </S.Tab>
          <S.Tab $active={activeTab === 'qr'} onClick={() => setActiveTab('qr')}>
            QR Section
          </S.Tab>
          <S.Tab $active={activeTab === 'ar'} onClick={() => setActiveTab('ar')}>
            AR Section
          </S.Tab>
          <S.Tab $active={activeTab === 'reading'} onClick={() => setActiveTab('reading')}>
            Reading Section
          </S.Tab>
          <S.Tab $active={activeTab === 'writing'} onClick={() => setActiveTab('writing')}>
            Writing Section
          </S.Tab>
          <S.Tab $active={activeTab === 'total'} onClick={() => setActiveTab('total')}>
            Total Score
          </S.Tab>
        </S.TabContainer>
      )}

      {/* Content */}
      <S.ContentArea>
        {!selectedTemplateId ? (
          <S.EmptyState>
            <h3>Select a Mock Test Template</h3>
            <p>Please select a mock test template from the dropdown above to begin score transformation analysis.</p>
          </S.EmptyState>
        ) : !originalScores ? (
          <S.EmptyState>
            <h3>Loading Data...</h3>
            <p>Loading original scores for analysis...</p>
          </S.EmptyState>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div>
                <h2>Transformation Overview</h2>
                {renderStatistics()}
                
                <div style={{ marginBottom: '30px' }}>
                  <h3>Template Information</h3>
                  <p><strong>Template Name:</strong> {selectedTemplate?.templateName || 'Unknown'}</p>
                  <p><strong>Total Students:</strong> {originalScores?.studentScores?.length || 0}</p>
                  <p><strong>Original Average:</strong> {originalScores?.summary?.averageScore?.toFixed(1) || 0}%</p>
                </div>
                
                {transformedData && (
                  <div>
                    <h3>Transformation Summary</h3>
                    <p><strong>Rank Preservation:</strong> {(transformedData.rankPreservationMetrics?.rank_preservation_rate * 100)?.toFixed(1) || 0}%</p>
                    <p><strong>Spearman Correlation:</strong> {transformedData.rankPreservationMetrics?.spearman_correlation?.toFixed(4) || 0}</p>
                    <p><strong>Max Rank Change:</strong> {transformedData.rankPreservationMetrics?.max_rank_change?.toFixed(1) || 0}</p>
                  </div>
                )}
                
                {/* Bulk Operations */}
                <div style={{ marginTop: '30px' }}>
                  <h3>Bulk Operations</h3>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <button
                      onClick={() => {
                        setStudentOffsets({});
                        setLocalOffsets({});
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        backgroundColor: '#ffc107',
                        color: 'black',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Clear All Offsets
                    </button>
                    <button
                      onClick={() => {
                        if (!transformedData) {
                          alert('No data available');
                          return;
                        }
                        
                        const totalAdjustments = transformedData.studentScores.reduce((count, student) => {
                          const hasSectionOffsets = ['QR', 'AR', 'Reading', 'Writing'].some(section => 
                            (student.transformedScores.sections[section]?.offset || 0) !== 0
                          );
                          const hasTotalOffset = (student.transformedScores.totalOffset || 0) !== 0;
                          return count + (hasSectionOffsets || hasTotalOffset ? 1 : 0);
                        }, 0);
                        
                        alert(`Total students with adjustments: ${totalAdjustments}`);
                      }}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      View Summary
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'qr' && (
              <div>
                <h2>QR Section Analysis</h2>
                {renderParameterControls('QR', 'QR Section')}
                {renderComparisonChart('QR', 'QR Section Scores')}
                {renderStudentOffsetTable('QR')}
              </div>
            )}

            {activeTab === 'ar' && (
              <div>
                <h2>AR Section Analysis</h2>
                {renderParameterControls('AR', 'AR Section')}
                {renderComparisonChart('AR', 'AR Section Scores')}
                {renderStudentOffsetTable('AR')}
              </div>
            )}

            {activeTab === 'reading' && (
              <div>
                <h2>Reading Section Analysis</h2>
                {renderParameterControls('Reading', 'Reading Section')}
                {renderComparisonChart('Reading', 'Reading Section Scores')}
                {renderStudentOffsetTable('Reading')}
              </div>
            )}

            {activeTab === 'writing' && (
              <div>
                <h2>Writing Section Analysis</h2>
                {renderParameterControls('Writing', 'Writing Section')}
                {renderComparisonChart('Writing', 'Writing Section Scores')}
                {renderStudentOffsetTable('Writing')}
              </div>
            )}

            {activeTab === 'total' && (
              <div>
                <h2>Total Score Analysis</h2>
                {renderParameterControls('total', 'Total Score')}
                {renderComparisonChart('total', 'Total Scores')}
                {renderStudentOffsetTable('total')}
              </div>
            )}
          </>
        )}
      </S.ContentArea>
    </S.Container>
  );
};

export default MockTestTransformation;